/**
 * Candle series storage.
 *
 * Columnar rather than row-of-objects: the chart renderer walks `high` and
 * `low` alone to find the visible range, and indicators walk `close` alone.
 * Columns mean those scans touch one contiguous buffer instead of striding
 * across 900 objects' worth of scattered heap.
 */

import { GrowableF64 } from '../lib/ring';
import { INTERVAL_MS, serverNow, type Interval, type RestKline } from './binance';

export class KlineSeries {
  readonly openTime = new GrowableF64(1024);
  readonly open = new GrowableF64(1024);
  readonly high = new GrowableF64(1024);
  readonly low = new GrowableF64(1024);
  readonly close = new GrowableF64(1024);
  readonly volume = new GrowableF64(1024);
  /** Taker-buy base volume — lets us colour volume bars by aggressor. */
  readonly buyVolume = new GrowableF64(1024);
  readonly trades = new GrowableF64(1024);

  /** Bumped on any mutation so renderers can skip clean frames. */
  seq = 0;
  /** True once the initial REST load has landed. */
  loaded = false;
  /** True while a history fetch is in flight, so we do not double-request. */
  loadingHistory = false;
  /** Set when we have paged back as far as the exchange has data. */
  historyExhausted = false;

  constructor(
    readonly symbol: string,
    readonly interval: Interval,
  ) {}

  get length(): number {
    return this.openTime.length;
  }

  get intervalMs(): number {
    return INTERVAL_MS[this.interval];
  }

  bar(i: number) {
    return {
      time: this.openTime.get(i),
      open: this.open.get(i),
      high: this.high.get(i),
      low: this.low.get(i),
      close: this.close.get(i),
      volume: this.volume.get(i),
      buyVolume: this.buyVolume.get(i),
      trades: this.trades.get(i),
    };
  }

  private pushRow(k: RestKline): void {
    this.openTime.push(k[0]);
    this.open.push(+k[1]);
    this.high.push(+k[2]);
    this.low.push(+k[3]);
    this.close.push(+k[4]);
    this.volume.push(+k[5]);
    this.buyVolume.push(+k[9]);
    this.trades.push(k[8]);
  }

  private clearAll(): void {
    this.openTime.clear();
    this.open.clear();
    this.high.clear();
    this.low.clear();
    this.close.clear();
    this.volume.clear();
    this.buyVolume.clear();
    this.trades.clear();
  }

  /** Initial load from REST. Replaces everything. */
  replaceAll(rows: RestKline[]): void {
    this.clearAll();
    for (let i = 0; i < rows.length; i++) this.pushRow(rows[i]);
    this.loaded = true;
    this.seq++;
  }

  /**
   * Prepend older bars fetched while panning left.
   *
   * Returns how many bars were actually added, which the caller needs in
   * order to shift the viewport by the same amount — otherwise the chart
   * appears to jump backwards the moment history arrives.
   */
  prepend(rows: RestKline[]): number {
    if (rows.length === 0) {
      this.historyExhausted = true;
      return 0;
    }
    const firstExisting = this.openTime.length > 0 ? this.openTime.get(0) : Infinity;
    // The exchange may overlap the boundary bar; keep strictly-older rows.
    let cut = rows.length;
    for (let i = 0; i < rows.length; i++) {
      if (rows[i][0] >= firstExisting) {
        cut = i;
        break;
      }
    }
    const add = rows.slice(0, cut);
    if (add.length === 0) {
      this.historyExhausted = true;
      return 0;
    }

    const n = add.length;
    const oldLen = this.openTime.length;
    const cols = [
      [this.openTime, 0],
      [this.open, 1],
      [this.high, 2],
      [this.low, 3],
      [this.close, 4],
      [this.volume, 5],
      [this.buyVolume, 9],
      [this.trades, 8],
    ] as const;

    for (const [col, field] of cols) {
      col.ensure(oldLen + n);
      col.data.copyWithin(n, 0, oldLen);
      for (let i = 0; i < n; i++) {
        const v = add[i][field];
        col.data[i] = typeof v === 'number' ? v : +v;
      }
      col.length = oldLen + n;
    }

    if (add.length < 400) this.historyExhausted = true;
    this.seq++;
    return n;
  }

  /**
   * Apply a live kline. Binance re-sends the in-progress bar many times a
   * second and then sends it once more with x=true when it closes, so this is
   * an upsert keyed on open time, not an append.
   */
  applyLive(k: {
    t: number;
    o: string;
    h: string;
    l: string;
    c: string;
    v: string;
    V: string;
    n: number;
  }): void {
    const n = this.openTime.length;
    const t = k.t;

    if (n > 0 && this.openTime.get(n - 1) === t) {
      const i = n - 1;
      this.open.set(i, +k.o);
      this.high.set(i, +k.h);
      this.low.set(i, +k.l);
      this.close.set(i, +k.c);
      this.volume.set(i, +k.v);
      this.buyVolume.set(i, +k.V);
      this.trades.set(i, k.n);
      this.seq++;
      return;
    }

    if (n > 0 && t < this.openTime.get(n - 1)) return; // stale, ignore

    // A new bar. If we missed intervals (tab asleep, socket down) the gap is
    // left as-is: the chart is index-spaced, and inventing empty candles would
    // be worse than a visible discontinuity in the time axis.
    this.openTime.push(t);
    this.open.push(+k.o);
    this.high.push(+k.h);
    this.low.push(+k.l);
    this.close.push(+k.c);
    this.volume.push(+k.v);
    this.buyVolume.push(+k.V);
    this.trades.push(k.n);
    this.seq++;
  }

  /**
   * Nudge the newest bar with a trade print. The kline stream only updates
   * every ~1-2s but trades arrive continuously, so without this the last
   * candle visibly lags the tape.
   */
  applyTrade(price: number, quantity: number, time: number, isBuy: boolean): void {
    const n = this.openTime.length;
    if (n === 0) return;
    const i = n - 1;
    const openT = this.openTime.get(i);
    if (time < openT || time >= openT + this.intervalMs) return; // belongs to another bar
    this.close.set(i, price);
    if (price > this.high.get(i)) this.high.set(i, price);
    if (price < this.low.get(i)) this.low.set(i, price);
    this.volume.set(i, this.volume.get(i) + quantity);
    if (isBuy) this.buyVolume.set(i, this.buyVolume.get(i) + quantity);
    this.trades.set(i, this.trades.get(i) + 1);
    this.seq++;
  }

  /** Index of the bar containing `time`, or the nearest one. -1 when empty. */
  indexOfTime(time: number): number {
    const n = this.openTime.length;
    if (n === 0) return -1;
    const a = this.openTime.data;
    let lo = 0;
    let hi = n - 1;
    if (time <= a[0]) return 0;
    if (time >= a[hi]) return hi;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (a[mid] === time) return mid;
      if (a[mid] < time) lo = mid + 1;
      else hi = mid - 1;
    }
    // `hi` is the last index with a[hi] < time.
    return hi;
  }

  /**
   * Fractional bar index for an arbitrary timestamp, so drawings anchored in
   * time land between candles rather than snapping to one.
   */
  fractionalIndex(time: number): number {
    const n = this.openTime.length;
    if (n === 0) return 0;
    const a = this.openTime.data;
    const step = this.intervalMs;
    if (time <= a[0]) return (time - a[0]) / step;
    if (time >= a[n - 1]) return n - 1 + (time - a[n - 1]) / step;
    const i = this.indexOfTime(time);
    const t0 = a[i];
    const t1 = i + 1 < n ? a[i + 1] : t0 + step;
    return i + (time - t0) / Math.max(1, t1 - t0);
  }

  /** Inverse of `fractionalIndex`, extrapolating past both ends. */
  timeAtIndex(index: number): number {
    const n = this.openTime.length;
    if (n === 0) return serverNow();
    const a = this.openTime.data;
    const step = this.intervalMs;
    if (index <= 0) return a[0] + index * step;
    if (index >= n - 1) return a[n - 1] + (index - (n - 1)) * step;
    const i = Math.floor(index);
    const frac = index - i;
    const t0 = a[i];
    const t1 = a[i + 1];
    return t0 + (t1 - t0) * frac;
  }
}
